// Types
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import dayjs from 'dayjs';
// Redux
import {
  fetchLogs,
  fetchTags,
  logsLoading,
  logsSelector,
  tagsSelector,
  totalPagesSelector,
  totalResultsSelector,
} from 'slices/logs.slice';
import { useAppDispatch } from 'slices/store';
import {
  ELoggedEventLevel,
  ELoggedEventType,
} from '@timeedit/types/lib/enums/loggedEvent.enum';
import type { TLoggedEvent } from '@timeedit/types/lib/types/loggedEvent.type';
import { EditableTable, TEInput, Tag } from '@timeedit/ui-components';
// Components
import {
  DatePicker,
  Form,
  type PaginationProps,
  Select,
  theme,
} from '@timeedit/ui-components/antd';
import FormItem from '@timeedit/ui-components/antd/form/FormItem/index';
import LogItemTable from './LogItemTable';
import RenderIcon from './RenderIcon';

const getTagColor = (tag: string) => {
  const tagToColorMap: Record<string, string> = {
    success: 'success',
    failure: 'error',
    critical: 'error',
    high: 'warning',
    medium: 'processing',
    low: 'default',
  };

  if (tag.toLowerCase() in tagToColorMap) {
    return tagToColorMap[tag.toLowerCase()];
  }
  return 'default';
};

const { useToken } = theme;

const LogsTable = () => {
  const { token } = useToken();
  const dispatch = useAppDispatch();
  const [form] = Form.useForm();

  /**
   * SELECTORS
   */
  const logs: TLoggedEvent[] = useSelector(logsSelector);
  const selectableTags = useSelector(tagsSelector);
  const totalPages = useSelector(totalPagesSelector);
  const totalResults = useSelector(totalResultsSelector);
  const isLoading = useSelector(logsLoading);

  /**
   * STATE
   */
  const [searchStr, setSearchStr] = useState<string>('');
  const [localLogs, setLocalLogs] = useState<TLoggedEvent[]>([]);
  const [level, setLogLevel] = useState<ELoggedEventLevel>(
    ELoggedEventLevel.LOW
  );
  const [start, setStart] = useState<number | undefined>(undefined);
  const [end, setEnd] = useState<number | undefined>(undefined);
  const [tags, setTags] = useState<string[] | undefined>([]);
  const [sortOrder, setSortOrder] = useState<string>('desc');
  const [page, setPage] = useState<number>(1);
  const [limit, setLimit] = useState<number>(10);
  const [pagination, setPagination] = useState<PaginationProps>({});

  /**
   * EFFECTS
   */

  useEffect(() => {
    dispatch(
      fetchTags(
        `/authenticate/logs/tags`,
        ELoggedEventType.SSO,
        level,
        start,
        end
      )
    );
  }, [dispatch, level, start, end]);

  useEffect(() => {
    setPage(1);
  }, [dispatch, level, tags, start, end]);

  useEffect(() => {
    dispatch(
      fetchLogs({
        path: `/authenticate/logs/find`,
        type: ELoggedEventType.SSO,
        level,
        limit,
        page,
        ...(tags?.length && { tags }),
        ...(start && { start }),
        ...(end && { end }),
        ...(sortOrder && { sortBy: `createdAt:${sortOrder}` }),
      })
    );
  }, [dispatch, level, start, end, tags, sortOrder, page, limit]);

  useEffect(() => {
    setPagination({
      current: page,
      pageSize: limit,
      total: totalResults,
      showSizeChanger: true,
      onChange: (page, pageSize) => {
        setPage(page);
        setLimit(pageSize);
      },
    });
  }, [totalPages, totalResults, page, limit]);

  useEffect(() => {
    if (searchStr === null || searchStr === undefined || searchStr === '') {
      setLocalLogs(logs);
      return;
    }
    setLocalLogs(
      logs.filter((log) =>
        JSON.stringify(log).toLowerCase().includes(searchStr.toLowerCase())
      )
    );
  }, [searchStr, logs]);

  const columns = [
    {
      title: 'Timestamp',
      dataIndex: 'createdAt',
      key: 'timestamp',
      width: '130px',
      render: (timestamp: number) =>
        dayjs(timestamp * 1000).format('YYYY-MM-DD HH:mm'),
    },
    {
      title: 'Message',
      dataIndex: 'label',
      key: 'message',
      ellipsis: true,
      render: (label: string) => {
        return label;
      },
    },
    {
      title: 'Level',
      dataIndex: 'level',
      key: 'level',
      width: '100px',
      render: (level: string) => {
        return (
          <Tag style={{ width: '100%' }} color={getTagColor(level)}>
            <center>{level}</center>
          </Tag>
        );
      },
    },
    {
      title: 'Tags',
      dataIndex: 'tags',
      key: 'tags',
      ellipsis: true,
      render: (tags: string[]) => {
        return tags.map((tag, index) => (
          <Tag
            key={index}
            style={{ marginInlineEnd: token.marginXS }}
            color={getTagColor(tag)}
          >
            {tag}
          </Tag>
        ));
      },
    },
  ];

  return (
    <Form
      layout="vertical"
      form={form}
      initialValues={{ type: ELoggedEventType.SSO }}
    >
      <div className="migration-table-wrapper">
        <div className="migration-table-header">
          <Form layout="inline">
            <FormItem label="Search">
              <TEInput.Input
                allowClear
                value={searchStr}
                onChange={(val) => setSearchStr(val)}
                style={{ minWidth: '130px' }}
              />
            </FormItem>
            <FormItem label="Created after">
              <DatePicker
                showTime
                size="small"
                style={{ borderRadius: '4px' }}
                value={start ? dayjs(start * 1000) : undefined}
                onChange={(date) => {
                  setStart(date ? date.unix() : undefined);
                }}
              />
            </FormItem>
            <FormItem label="Created before">
              <DatePicker
                showTime
                size="small"
                style={{ borderRadius: '4px' }}
                value={end ? dayjs(end * 1000) : undefined}
                onChange={(date) => setEnd(date ? date.unix() : undefined)}
              />
            </FormItem>
            <FormItem label="Minimum level">
              <Select
                size="small"
                style={{ minWidth: '130px' }}
                value={level}
                onChange={(value) => setLogLevel(value)}
                options={Object.values(ELoggedEventLevel).map((level) => ({
                  label: level,
                  value: level,
                }))}
              />
            </FormItem>
            <FormItem label="Tags">
              <Select
                style={{ minWidth: '130px' }}
                mode="multiple"
                allowClear
                size="small"
                value={tags}
                options={selectableTags.map((tag) => ({
                  label: tag,
                  value: tag,
                }))}
                onChange={(value) => setTags(value)}
              />
            </FormItem>
            <Form.Item label="Sort order" style={{ minWidth: '130px' }}>
              <Select
                size="small"
                onChange={(value) => setSortOrder(value)}
                value={sortOrder}
                defaultValue={'desc'}
                options={[
                  { label: 'ASC', value: 'asc' },
                  { label: 'DESC', value: 'desc' },
                ]}
              ></Select>
            </Form.Item>
          </Form>
        </div>
        <EditableTable
          columns={columns}
          dataSource={localLogs}
          pagination={pagination}
          rowKey={(loggedEvent: TLoggedEvent) => loggedEvent.id}
          expandable={{
            expandedRowRender: (record: TLoggedEvent) => (
              <LogItemTable record={record} isLoading={isLoading} />
            ),
            rowExpandable: (record) => record.items?.length > 0,
            expandIcon: ({ expanded, onExpand, record }) => (
              <RenderIcon
                expanded={expanded}
                onExpand={onExpand}
                record={record}
              />
            ),
          }}
        />
      </div>
    </Form>
  );
};

export default LogsTable;
